import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../AuthContext';
import { app } from '../firebase';
import {
  getFirestore, doc, getDoc, addDoc, collection, serverTimestamp,
} from 'firebase/firestore';
import { diagnosePlateauArchetype } from '../services/aiAnalysisService';
import { plateauArchetypes } from '../data/plateauArchetypes';
import { ArrowLeft, Zap, ChevronRight, ChevronDown } from 'lucide-react';
import { IconTrophy, IconRocket, IconFlame, IconBolt, IconCheck } from '../components/common/Icons.jsx';

const db = getFirestore(app);

const COMMON_INSIGHT = {
  title: 'あなたの壁は、かつての強みの副作用です',
  body: '「わかっているのにできない」のは意志力の問題ではありません。今あなたを止めている行動パターンは、過去に実際に機能した適応戦略です。壁は弱点ではなく、7割正しい行動の副作用です。完全に間違った行動を変えるのは簡単ですが、7割正しい行動を変えるのが最も難しい理由がここにあります。',
  key: 'このプランは「やめる」ためのものではありません。新しい成功体験を積み重ねることで、あなたの脳が自然と次のパターンに移行することを目的としています。',
  types: [
    { emoji: '🧠', name: '論理的な設計者', pattern: '考え抜くことで良い結果を出してきた' },
    { emoji: '🤝', name: '共感的な連結者', pattern: '他者優先で信頼・居場所を得てきた' },
    { emoji: '🚀', name: '大胆な開拓者',   pattern: '単独行動で速く結果を出してきた' },
    { emoji: '🔥', name: '柔軟な実践者',   pattern: '場当たり対応で乗り越えてきた' },
  ],
};

const BREAKTHROUGH_PLANS = {
  LOGICAL_ARCHITECT: {
    color: '#6366f1', gradient: 'linear-gradient(135deg,#6366f1,#8b5cf6)',
    bgLight: '#eeedfe', emoji: '🧠',
    tagline: '思考の迷宮から抜け出し、行動で突破する7日間',
    whyTitle: 'なぜ突破できないのか',
    why: '「計画しすぎ」と自覚しているのに、計画をやめられない。これは意志力の問題ではなく、不安管理の問題です。計画している間は「失敗していない状態」が保たれます。動き出した瞬間に「失敗する可能性」が生まれる。つまり計画は失敗回避のための安全地帯として機能しています。「もう少し考えれば完璧になるはず」という合理化が知性と共存してしまうのです。',
    overwrite: '「行動してうまくいった」という体験を7日間で積み重ね、行動が安全だという新しい回路を作ります',
    days: [
      { day:1, theme:'15分行動の成功体験を作る', ow:'「動いても大丈夫だった」という記憶を作る', action:'今日1つだけ、計画を15分で切り上げてそのまま実行する。結果がどうであれ「動いた事実」を記録する。', point:'脳は「行動→生存」の体験を繰り返すことで、行動を安全として再分類します' },
      { day:2, theme:'不完全な成果物を誰かに見せる', ow:'「不完全でも価値を生み出せた」という体験', action:'60%の完成度のものを今日1人に見せる。フィードバックをもらい、それで改善できたことを確認する。', point:'外部からの反応が「完璧でなくても受け入れられる」という証拠として蓄積されます' },
      { day:3, theme:'直感判断→成功の記録をつける', ow:'直感判断の成功率を自分で実証する', action:'今日の小さな決断を3秒で決め、結果を夜にメモする。「直感で決めてうまくいった回数」を数える。', point:'成功の記録が「直感でも十分機能する」という新しい証拠になります' },
      { day:4, theme:'誤りから学んだ成功体験を作る', ow:'「失敗は修正できる」という安心感の体験', action:'今日うまくいかなかったことを1つ選び、修正して再実行する。「失敗→修正→改善」の1サイクルを完成させる。', point:'失敗が取り返しのつかないものではないという実体験が、行動への恐怖を解除します' },
      { day:5, theme:'他者の反応を証拠にする', ow:'「行動した自分は受け入れられている」という証拠収集', action:'今日までに動いた結果に対して、他者から受け取ったポジティブな反応を3つ書き出す。', point:'社会的承認の記録が、行動の安全性を強化します' },
      { day:6, theme:'計画なしで1時間動いてみる', ow:'「計画なしでも前に進める」という新しい成功パターン', action:'1時間、計画を立てずに「今できること」だけで動く。終了後に成果を確認する。', point:'計画がなくても機能できたという体験が、計画への依存を自然に緩めます' },
      { day:7, theme:'7日間の行動成功率を振り返る', ow:'「行動する自分」というアイデンティティの確立', action:'今週「動いた回数」と「うまくいった割合」を数える。完璧でなくても前進できた証拠を確認する。', point:'7日間の証拠の積み重ねが、新しい自己イメージの土台になります' },
    ],
  },
  EMPATHETIC_CONNECTOR: {
    color: '#10b981', gradient: 'linear-gradient(135deg,#10b981,#059669)',
    bgLight: '#eaf3de', emoji: '🤝',
    tagline: '他者への貢献を自分の成長エンジンに変える7日間',
    whyTitle: 'なぜ突破できないのか',
    why: '「断れない」と自覚しているのに、断れない。これはアイデンティティの問題です。「役に立つ人」「必要とされる人」であることが自己概念の核になっている。断ることは相手を傷つける以前に、自分が何者かを失う感覚に近い。専門性を磨く必要性は頭でわかっていても、それは「将来の自分」の話。今目の前で困っている人への貢献は「現在の自分」の存在証明です。',
    overwrite: '「専門性を持つ自分がより深く貢献できた」体験を作り、成長が他者への貢献につながる回路を構築します',
    days: [
      { day:1, theme:'専門性が誰かを助けた体験を掘り起こす', ow:'「専門性も貢献の形である」という記憶の再発見', action:'過去に自分の「得意なこと」で誰かが喜んでくれた瞬間を3つ思い出して書く。', point:'過去の成功体験を再解釈することで、専門性と貢献が矛盾しないという新しい枠組みを作ります' },
      { day:2, theme:'深く学んだ内容で誰かを助ける', ow:'「学ぶ→より深く貢献できる」という成功の連鎖体験', action:'今日90分だけ自分のテーマを学び、そこで得た知識を夜に1人にシェアする。', point:'学びが直接貢献につながる体験が、専門性への投資を利他的として再分類します' },
      { day:3, theme:'断ったことで相手が成長した体験を作る', ow:'「断ることが相手の成長を促す場合がある」という体験', action:'1つ依頼を断り「自分でやってみてください」と伝える。翌日以降その人がどうなったかを観察する。', point:'断りが相手の自立を助けるケースの実体験が、罪悪感の根拠を崩します' },
      { day:4, theme:'自分の意見が採用された成功体験を作る', ow:'「自分の軸を示すことで貢献できた」という体験', action:'今日の議論で自分の考えを最初に述べる。その意見が何らかの形で活用されたら記録する。', point:'自分の視点が価値を持つという実証が、他者優先の自動反応を緩めます' },
      { day:5, theme:'専門的に深い貢献ができた瞬間を作る', ow:'「深い貢献は広い貢献より喜ばれることがある」という発見', action:'今日は自分の得意分野の質問だけに全力で答える。広く浅くではなく1点突破で。', point:'専門的貢献の質の高さを実感することで、広く助けることへの執着が自然と緩みます' },
      { day:6, theme:'自分のための時間が貢献の質を上げた体験', ow:'「充電した自分のほうが、より良く助けられる」という体験', action:'午前中を完全に自分の学習にあて、午後に誰かを助ける。2つの質の違いを感じる。', point:'自己投資が他者貢献の質を高めるという直接体験が、罪悪感の構造を書き換えます' },
      { day:7, theme:'7日間で生まれた貢献の質の変化を確認する', ow:'「専門性を持つ自分」という新しいアイデンティティの定着', action:'今週「専門性を活かして誰かを助けた瞬間」を振り返る。以前の自分との違いを1文で書く。', point:'一週間の証拠が「役に立つ人」の定義を拡張し、より豊かな自己概念を形成します' },
    ],
  },
  BOLD_PIONEER: {
    color: '#ef4444', gradient: 'linear-gradient(135deg,#ef4444,#dc2626)',
    bgLight: '#fcebeb', emoji: '🚀',
    tagline: '推進力を活かしながら、チームと共に突破する7日間',
    whyTitle: 'なぜ突破できないのか',
    why: '「周囲を巻き込めていない」と自覚しているのに、一人で動いてしまう。これはスピードへの依存です。過去に「一人で動いたほうが速かった」成功体験が強化学習として蓄積されているため、チームを信頼することのコストが体感的に高い。「自分が正しい」という自信が強いほど、他者の反対意見を無駄なノイズとして処理しやすくなります。',
    overwrite: '「チームと動いたほうが速く遠くへ行けた」体験を積み重ね、協働が自分の推進力を増幅させると実証します',
    days: [
      { day:1, theme:'相談して判断が改善した体験を作る', ow:'「相談によって判断が良くなった」という直接体験', action:'今日の重要な決断を1人に相談してから実行する。相談前と後の決断の質の差を夜にメモする。', point:'相談コストより判断改善のベネフィットが大きいという実証が、相談への抵抗を緩めます' },
      { day:2, theme:'チームの情報が成果を上げた体験を作る', ow:'「チームの情報が自分の推進力を強化した」という体験', action:'メンバー1人に「最近気になっていることは？」と聞き、その情報を今日の意思決定に活かす。', point:'情報収集が判断精度を上げるという実体験が、他者からの情報価値を再評価させます' },
      { day:3, theme:'任せた結果が予想以上だった体験を作る', ow:'「任せることで自分の時間と質が同時に上がった」体験', action:'小さなタスクを1つ完全にメンバーに任せる。「自分がやるより良かった点」を1つ見つける。', point:'デリゲーションの成功体験が、任せることへの不安を具体的な信頼に変えます' },
      { day:4, theme:'チームが自律的に動いた瞬間を作る', ow:'「WHYの共有がチームを自分の代わりに動かした」体験', action:'「なぜこうするか」を今日1つの判断で丁寧に説明する。翌日チームが同じ方向で動いているか確認する。', point:'背景共有の効果を実感することで、説明コストが投資として認識されるようになります' },
      { day:5, theme:'反対意見が結果を改善した体験を作る', ow:'「反対意見が成果の質を上げた」という体験', action:'今日の提案に対して反対意見を1つ積極的に引き出す。その視点を取り入れて修正し、結果を確認する。', point:'批判が成果改善に機能するという実証が、他者意見をノイズではなくシグナルとして処理させます' },
      { day:6, theme:'チームと動いて一人より速かった体験を作る', ow:'「協働が単独行動より速い場面がある」という実証', action:'今日のタスクを意識的にチームで分担する。一人でやった場合の推定時間と比較する。', point:'スピード比較の実体験が、一人行動への強化学習を書き換え始めます' },
      { day:7, theme:'7日間で広がった射程を確認する', ow:'「チームと自分の推進力を掛け合わせた新しい自分」の実感', action:'今週チームと動いた結果、一人では到達できなかった成果を1つ特定して記録する。', point:'到達距離の拡大という具体的証拠が、次の行動スタイルを変える最大の動機になります' },
    ],
  },
  FLEXIBLE_ARTISAN: {
    color: '#f59e0b', gradient: 'linear-gradient(135deg,#f59e0b,#d97706)',
    bgLight: '#faeeda', emoji: '🔥',
    tagline: '現場力を体系化し、再現可能な成長システムを作る7日間',
    whyTitle: 'なぜ突破できないのか',
    why: '「計画が続かない」と自覚しているのに、計画を立てない。これは即時報酬への最適化です。計画を立てることの報酬は遠い未来にあり、目の前の問題を解決することの報酬は今すぐ得られる。「その場での対応力」で実際に結果を出してきたため、計画不要の成功体験が豊富にある。計画が崩れた過去の挫折感から「どうせ続かない」という学習性無力感が計画行動を抑制しています。',
    overwrite: '「小さな計画が実際に機能した」成功体験を積み、計画が自分を助けるという新しい証拠を作り続けます',
    days: [
      { day:1, theme:'今日の行動を振り返り、パターンを発見する', ow:'「振り返りが次の行動を改善した」という最初の体験', action:'今日やったことを5つ書き出し、「なぜうまくいったか」を1つだけ分析する。', point:'過去の行動の中に既にある成功パターンを発見することで、体系化が自分の中から生まれるものになります' },
      { day:2, theme:'3項目だけの計画を立てて実行する', ow:'「小さな計画は崩れなかった」という成功体験', action:'明日やること3つだけを今夜書く。翌日その3つを実行し「計画通りにできた」を確認する。', point:'失敗しないサイズの計画で成功体験を作ることが、「どうせ続かない」という学習性無力感を解除します' },
      { day:3, theme:'自分の対応力を「仕組み」として書き出す', ow:'「自分の経験が再利用可能な知恵になった」体験', action:'今日うまく対処できた場面を1つ選び「次に同じ状況が来たらどうするか」を1段落で書く。', point:'暗黙知の言語化が、蓄積できないという感覚を「蓄積できている実感」に変えます' },
      { day:4, theme:'計画が自分を助けた瞬間を意識的に作る', ow:'「計画は制約ではなく道具だった」という体験', action:'前夜に書いた3項目リストを見ながら朝を始める。「リストがあって助かった」と感じた瞬間をメモする。', point:'計画が自分を助けた具体的瞬間の記憶が、計画への感情的障壁を取り除きます' },
      { day:5, theme:'過去の成功を未来の計画に変換する', ow:'「経験が再現可能な戦略になった」喜びの体験', action:'過去にうまくいったことを1つ選び「同じ成功を再現するための手順」を3ステップで書く。', point:'成功の再現性を実感することで、体系化が喪失ではなく増幅として認識されます' },
      { day:6, theme:'「即興」と「計画」を組み合わせた成功を作る', ow:'計画と即興は対立しないという体験', action:'今日は計画した枠組みの中で自由に即興する。「計画があったから良い即興ができた」と感じたら記録する。', point:'構造が創造性を制約するのではなく支える体験が、計画への抵抗感を根本から変えます' },
      { day:7, theme:'7日間で生まれた「自分の方法論」を確認する', ow:'「体系化された自分の方法論を持っている」という新しいアイデンティティ', action:'今週の行動から生まれた「自分なりのパターン・仕組み」を3つ書き出す。それが自分の資産であることを認識する。', point:'1週間で蓄積した具体的な証拠が、場当たり型から戦略型への移行を自然なものにします' },
    ],
  },
};

const BreakthroughPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [archetype, setArchetype] = useState(null);
  const [plan, setPlan] = useState(null);
  const [loading, setLoading] = useState(true);
  const [completedDays, setCompletedDays] = useState([]);
  const [expandedDay, setExpandedDay] = useState(null);
  const [insightOpen, setInsightOpen] = useState(true);
  const [whyOpen, setWhyOpen] = useState(true);
  const [nickname, setNickname] = useState('');
  const [posting, setPosting] = useState(false);
  const [postedDays, setPostedDays] = useState([]);
  const [pace, setPace] = useState(1); // 0.5/0.75/1/1.5/2

  useEffect(() => {
    if (!user) return;
    const fetchData = async () => {
      try {
        const userDoc = await getDoc(doc(db, 'users', user.uid));
        const profile = userDoc.exists() ? userDoc.data() : {};
        setNickname(profile.nickname || profile.username || profile.displayName || user?.displayName || '');
        const key = diagnosePlateauArchetype(profile);
        setArchetype({ key, ...plateauArchetypes[key] });
        setPlan(BREAKTHROUGH_PLANS[key]);
      } catch (e) {
        setArchetype({ key: 'FLEXIBLE_ARTISAN', ...plateauArchetypes['FLEXIBLE_ARTISAN'] });
        setPlan(BREAKTHROUGH_PLANS['FLEXIBLE_ARTISAN']);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
    const saved = localStorage.getItem('nobishiro_breakthrough_days');
    if (saved) setCompletedDays(JSON.parse(saved));
    const postedSaved = localStorage.getItem('nobishiro_breakthrough_posted');
    if (postedSaved) setPostedDays(JSON.parse(postedSaved));
    const paceSaved = localStorage.getItem('nobishiro_breakthrough_pace');
    if (paceSaved) setPace(parseFloat(paceSaved));
  }, [user]);

  // ✅ Day完了時にFirestoreへ自動投稿
  const postToFeed = async (dayNum, archetypeKey, streak) => {
    if (!user || posting) return;
    if (postedDays.includes(dayNum)) return; // 重複投稿防止
    setPosting(true);
    try {
      await addDoc(collection(db, 'breakthroughs'), {
        uid: user.uid,
        nickname: nickname || user?.displayName || null,
        displayName: user?.displayName || null,
        username: nickname || null,
        archetypeKey,
        dayNum,
        streak,
        reactions: [],
        createdAt: serverTimestamp(),
      });
      const newPosted = [...postedDays, dayNum];
      setPostedDays(newPosted);
      localStorage.setItem('nobishiro_breakthrough_posted', JSON.stringify(newPosted));
    } catch (e) {
      console.error('Feed post error:', e);
    } finally {
      setPosting(false);
    }
  };

  const toggleDay = async (day) => {
    const isDone = completedDays.includes(day);
    let next;
    if (isDone) {
      next = completedDays.filter(d => d !== day);
    } else {
      next = [...completedDays, day];
      // 連続日数を計算
      const sortedDays = [...next].sort((a,b) => b - a);
      let streak = 0;
      for (let i = 0; i < sortedDays.length; i++) {
        if (sortedDays[i] === sortedDays[0] - i) streak++;
        else break;
      }
      // Firestoreへ投稿
      if (archetype) {
        await postToFeed(day, archetype.key, streak);
      }
    }
    setCompletedDays(next);
    localStorage.setItem('nobishiro_breakthrough_days', JSON.stringify(next));
  };

  if (loading) return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f9fafb' }}>
      <div style={{ textAlign: 'center' }}>
        <div style={{ width: 44, height: 44, border: '3px solid #e5e7eb', borderTopColor: '#6366f1', borderRadius: '50%', animation: 'spin 0.8s linear infinite', margin: '0 auto 12px' }} />
        <p style={{ color: '#6b7280', fontSize: 14 }}>あなたのタイプを診断中...</p>
      </div>
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );

  const completedCount = completedDays.length;
  const progressPct = Math.round((completedCount / 7) * 100);

  return (
    <div style={{ minHeight: '100vh', background: '#f9fafb', paddingBottom: 60 }}>
      {/* ヘッダー */}
      <div style={{ background: plan.gradient, color: '#fff', padding: '20px 16px 28px' }}>
        <button onClick={() => navigate('/dashboard')}
          style={{ background: 'rgba(255,255,255,0.2)', border: 'none', borderRadius: 8, padding: '6px 12px', color: '#fff', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, fontWeight: 600, marginBottom: 20 }}>
          <ArrowLeft size={14} /> ダッシュボード
        </button>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 10 }}>
          <div style={{ width: 48, height: 48, borderRadius: 14, background: 'rgba(255,255,255,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            {plan.emoji === '🧠' ? <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M9.5 2A2.5 2.5 0 0 1 12 4.5v15a2.5 2.5 0 0 1-4.96-.44 2.5 2.5 0 0 1-2.96-3.08 3 3 0 0 1-.34-5.58 2.5 2.5 0 0 1 1.32-4.24 2.5 2.5 0 0 1 4.44-1.66z"/><path d="M14.5 2A2.5 2.5 0 0 0 12 4.5v15a2.5 2.5 0 0 0 4.96-.44 2.5 2.5 0 0 0 2.96-3.08 3 3 0 0 0 .34-5.58 2.5 2.5 0 0 0-1.32-4.24 2.5 2.5 0 0 0-4.44-1.66z"/></svg>
            : plan.emoji === '🤝' ? <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M20.42 4.58a5.4 5.4 0 0 0-7.65 0l-.77.78-.77-.78a5.4 5.4 0 0 0-7.65 0C1.46 6.7 1.33 10.28 4 13l8 8 8-8c2.67-2.72 2.54-6.3.42-8.42z"/></svg>
            : plan.emoji === '🚀' ? <IconRocket size={28} color="#fff"/>
            : <IconFlame size={28} color="#fff"/>}
          </div>
          <div>
            <div style={{ fontSize: 11, opacity: 0.8, fontWeight: 600, letterSpacing: 1, marginBottom: 2 }}>ブレイクスルー突破プラン</div>
            <div style={{ fontSize: 20, fontWeight: 800 }}>{archetype.name}</div>
          </div>
        </div>
        {nickname && (
          <div style={{ fontSize: 12, opacity: 0.9, marginBottom: 8 }}>
            👤 {nickname}さんのプラン
          </div>
        )}
        <p style={{ fontSize: 13, opacity: 0.9, lineHeight: 1.7, margin: '0 0 16px' }}>{plan.tagline}</p>
        {/* ペース選択 */}
        <div style={{ display: 'flex', gap: 6, marginBottom: 12, flexWrap: 'wrap' }}>
          {[
            { val: 0.5,  label: '0.5x', desc: '14日間・じっくり', icon: '🐢' },
            { val: 0.75, label: '0.75x', desc: '9日間・ゆったり', icon: '🚶' },
            { val: 1,    label: '1x',   desc: '7日間・標準', icon: '🏃' },
            { val: 1.5,  label: '1.5x', desc: '5日間・速め', icon: '⚡' },
            { val: 2,    label: '2x',   desc: '4日間・集中', icon: '🔥' },
          ].map(p => (
            <button key={p.val} onClick={() => { setPace(p.val); localStorage.setItem('nobishiro_breakthrough_pace', String(p.val)); }}
              style={{ padding: '5px 10px', borderRadius: 20, border: 'none', cursor: 'pointer', fontSize: 11, fontWeight: 700,
                background: pace === p.val ? 'rgba(255,255,255,0.9)' : 'rgba(255,255,255,0.2)',
                color: pace === p.val ? plan.color : '#fff', transition: 'all 0.15s' }}>
              {p.icon} {p.label}
            </button>
          ))}
        </div>
        <div style={{ background: 'rgba(255,255,255,0.15)', borderRadius: 10, padding: '12px 14px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
            <span style={{ fontSize: 12, fontWeight: 700 }}>
              {pace === 0.5 ? '🐢 14日間プラン' : pace === 0.75 ? '🚶 9日間プラン' : pace === 1 ? '🏃 7日間プラン（標準）' : pace === 1.5 ? '⚡ 5日間プラン' : '🔥 4日間プラン（集中）'}
            </span>
            <span style={{ fontSize: 13, fontWeight: 800 }}>{completedCount}/7ステップ完了</span>
          </div>
          <div style={{ background: 'rgba(255,255,255,0.3)', borderRadius: 99, height: 7, overflow: 'hidden' }}>
            <div style={{ background: '#fff', height: '100%', width: `${progressPct}%`, borderRadius: 99, transition: 'width 0.4s ease' }} />
          </div>
          {completedCount === 7 && (
            <div style={{ textAlign: 'center', marginTop: 8, fontSize: 13, fontWeight: 700 }}>🏆 全7ステップ突破！おめでとうございます！</div>
          )}
        </div>
      </div>

      <div style={{ maxWidth: 680, margin: '0 auto', padding: '20px 16px' }}>
        {/* 共通インサイト */}
        {[
          {
            open: insightOpen, toggle: () => setInsightOpen(!insightOpen),
            borderColor: '#7f77dd', subLabel: 'はじめに読んでください',
            title: COMMON_INSIGHT.title,
            content: (
              <>
                <p style={{ fontSize: 13, color: '#374151', lineHeight: 1.85, margin: '0 0 12px' }}>{COMMON_INSIGHT.body}</p>
                <div style={{ background: '#f0f9ff', borderRadius: 10, padding: '11px 14px', borderLeft: '3px solid #38bdf8', marginBottom: 14 }}>
                  <p style={{ fontSize: 13, color: '#0369a1', margin: 0, lineHeight: 1.8, fontWeight: 500 }}>{COMMON_INSIGHT.key}</p>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                  {COMMON_INSIGHT.types.map(t => (
                    <div key={t.name} style={{ background: '#f9fafb', borderRadius: 10, padding: '10px 12px', border: '1px solid #e5e7eb' }}>
                      <div style={{ fontSize: 12, fontWeight: 700, color: '#374151', marginBottom: 3 }}>{t.emoji} {t.name}</div>
                      <div style={{ fontSize: 11, color: '#6b7280', lineHeight: 1.6 }}>{t.pattern}</div>
                    </div>
                  ))}
                </div>
              </>
            ),
          },
          {
            open: whyOpen, toggle: () => setWhyOpen(!whyOpen),
            borderColor: plan.color, subLabel: 'あなたのタイプの場合',
            title: plan.whyTitle,
            content: (
              <>
                <p style={{ fontSize: 13, color: '#374151', lineHeight: 1.85, margin: '0 0 12px' }}>{plan.why}</p>
                <div style={{ background: plan.bgLight, borderRadius: 10, padding: '11px 14px', borderLeft: `3px solid ${plan.color}` }}>
                  <div style={{ fontSize: 11, fontWeight: 700, color: plan.color, marginBottom: 4 }}>このプランのアプローチ</div>
                  <p style={{ fontSize: 13, color: '#374151', margin: 0, lineHeight: 1.8 }}>{plan.overwrite}</p>
                </div>
              </>
            ),
          },
        ].map((s, i) => (
          <div key={i} style={{ background: '#fff', borderRadius: 16, border: '1.5px solid #e5e7eb', marginBottom: 14, overflow: 'hidden', boxShadow: '0 2px 8px rgba(0,0,0,0.04)' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '15px 18px', cursor: 'pointer', borderLeft: `4px solid ${s.borderColor}` }} onClick={s.toggle}>
              <div>
                <div style={{ fontSize: 11, fontWeight: 700, color: s.borderColor, marginBottom: 3 }}>{s.subLabel}</div>
                <div style={{ fontSize: 15, fontWeight: 700, color: '#111827' }}>{s.title}</div>
              </div>
              {s.open ? <ChevronDown size={18} color="#9ca3af" /> : <ChevronRight size={18} color="#9ca3af" />}
            </div>
            {s.open && <div style={{ padding: '0 18px 18px' }}>{s.content}</div>}
          </div>
        ))}

        <h2 style={{ fontSize: 15, fontWeight: 800, color: '#111827', margin: '0 0 12px', display: 'flex', alignItems: 'center', gap: 7 }}>
          <Zap size={15} color={plan.color} fill={plan.color} />
          新しい成功体験を積む7ステップ
          <span style={{ fontSize: 11, fontWeight: 600, color: '#9ca3af', marginLeft: 4 }}>
            （{pace === 0.5 ? '14日間' : pace === 0.75 ? '9日間' : pace === 1 ? '7日間' : pace === 1.5 ? '5日間' : '4日間'}で完走）
          </span>
        </h2>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {plan.days.map((d) => {
            const done = completedDays.includes(d.day);
            const expanded = expandedDay === d.day;
            return (
              <div key={d.day} style={{
                background: '#fff', borderRadius: 14,
                border: `${done ? '1.5px' : '0.5px'} solid ${done ? plan.color + '70' : '#e5e7eb'}`,
                overflow: 'hidden',
                boxShadow: done ? `0 2px 10px ${plan.color}18` : 'none',
              }}>
                <div style={{ display: 'flex', alignItems: 'center', padding: '13px 16px', cursor: 'pointer', gap: 10 }}
                  onClick={() => setExpandedDay(expanded ? null : d.day)}>
                  <button onClick={(e) => { e.stopPropagation(); toggleDay(d.day); }}
                    style={{
                      background: done ? plan.color : 'none',
                      border: `2px solid ${done ? plan.color : '#d1d5db'}`,
                      borderRadius: '50%', width: 24, height: 24,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      cursor: 'pointer', flexShrink: 0, padding: 0,
                    }}>
                    {done && <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>}
                  </button>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
                      <span style={{ background: done ? plan.color : '#f3f4f6', color: done ? '#fff' : '#9ca3af', borderRadius: 6, padding: '2px 8px', fontSize: 11, fontWeight: 700, flexShrink: 0 }}>
                        Step {d.day}
                      </span>
                      <span style={{ fontSize: 10, color: done ? plan.color : '#d1d5db', fontWeight: 600, flexShrink: 0 }}>
                        {pace === 1 ? `${d.day}日目` : pace === 2 ? `${Math.ceil(d.day/2)}日目` : pace === 1.5 ? `${Math.ceil(d.day/1.5)}日目` : pace === 0.75 ? `${Math.ceil(d.day/0.75)}日目` : `${d.day*2}日目`}に実施
                      </span>
                      <span style={{ fontSize: 13, fontWeight: 700, color: done ? plan.color : '#111827', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{d.theme}</span>
                    </div>
                    {!expanded && <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 3, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>上書き：{d.ow}</div>}
                  </div>
                  <ChevronRight size={15} color="#9ca3af" style={{ transform: expanded ? 'rotate(90deg)' : 'none', transition: 'transform 0.2s', flexShrink: 0 }} />
                </div>
                {expanded && (
                  <div style={{ padding: '0 16px 16px', borderTop: '1px solid #f3f4f6' }}>
                    {[
                      { label: '上書きする成功体験', text: d.ow, bg: plan.bgLight, borderColor: plan.color, labelColor: plan.color },
                      { label: '今日のアクション',   text: d.action, bg: '#f8faff', borderColor: '#6366f1', labelColor: '#6366f1' },
                      { label: 'なぜこれが脳に効くか', text: d.point, bg: '#f0fdf4', borderColor: '#10b981', labelColor: '#059669' },
                    ].map((box, bi) => (
                      <div key={bi} style={{ background: box.bg, borderRadius: 10, padding: '11px 14px', marginBottom: 10, marginTop: bi === 0 ? 12 : 0, borderLeft: `3px solid ${box.borderColor}` }}>
                        <div style={{ fontSize: 11, fontWeight: 700, color: box.labelColor, marginBottom: 4 }}>{box.label}</div>
                        <p style={{ fontSize: 13, color: '#374151', margin: 0, lineHeight: 1.8 }}>{box.text}</p>
                      </div>
                    ))}
                    <button onClick={() => toggleDay(d.day)}
                      style={{
                        width: '100%',
                        background: done ? '#f3f4f6' : plan.gradient,
                        color: done ? '#6b7280' : '#fff',
                        border: 'none', borderRadius: 10, padding: '11px',
                        fontSize: 13, fontWeight: 700, cursor: 'pointer',
                        display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
                      }}>
                      {done
                        ? <><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{marginRight:4}}><polyline points="20 6 9 17 4 12"/></svg>Step {d.day} 完了済み</>
                        : <>Step {d.day} の成功体験を記録する{posting ? '（投稿中...）' : ''}</>
                      }
                    </button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
};

export default BreakthroughPage;
